import type { ProfitLoss } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useApiResource } from '#client/hooks/useResourceItem';
import { formatMoney } from '#client/lib/format';
import { ENDPOINT, withQuery } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import { Link } from 'react-router';
import { DateRangeBar } from './DateRangeBar';
import { marginPercent } from './reports.logic';
import { useDateRange } from './useDateRange';

/**
 * What the lab earned, less what it spent.
 *
 * The most important element on this screen is the **strip naming the costs
 * nobody recorded**. A profit-and-loss that quietly omits salaries is worse than
 * none: the first month shows an absurd margin, somebody notices, and after that
 * the screen is never trusted again. So an empty category is named, with a link
 * to the form that fills it, rather than being treated as a zero.
 */
export function ProfitLossPage() {
  const range = useDateRange();
  const report = useApiResource<ProfitLoss>(
    withQuery(ENDPOINT.profitLoss, { from: range.from, to: range.to }),
  );

  if (report.loading) return <PageLoading title="Profit and loss" />;
  if (report.error || !report.data) {
    return (
      <PageMessage title="Profit and loss">
        {report.error ?? 'Could not load the statement.'}
      </PageMessage>
    );
  }

  const statement = report.data;

  return (
    <Page title="Profit and loss" subtitle={`${range.from} to ${range.to}`}>
      <div className="c-form-stack">
        <Card>
          <DateRangeBar range={range} />
        </Card>
        {statement.missing.length > 0 && <MissingCostsBanner missing={statement.missing} />}
        <StatementCard statement={statement} />
        <ExclusionsCard />
      </div>
    </Page>
  );
}

function MissingCostsBanner({ missing }: { missing: ProfitLoss['missing'] }) {
  return (
    <Banner tone="warning" title="Some costs are not recorded for this period">
      <p>
        Nothing has been entered under {missing.map((item) => item.label).join(' or ')}. The surplus
        below is therefore higher than the lab's real result — treat it as an upper bound until
        those are entered.
      </p>
      <p>
        <Link to={routes.expenseNew}>Record an expense</Link>
      </p>
    </Banner>
  );
}

function StatementCard({ statement }: { statement: ProfitLoss }) {
  const { billed, collected, expenses, total_expenses, surplus } = statement;
  const margin = marginPercent(surplus, billed);

  return (
    <Card title="Statement">
      <table className="c-statement">
        <tbody>
          <tr className="c-statement__row">
            <th scope="row">Billed</th>
            <td className="tnum">{formatMoney(billed)}</td>
          </tr>
          <tr className="c-statement__row c-statement__row--muted">
            <th scope="row">of which collected</th>
            <td className="tnum">{formatMoney(collected)}</td>
          </tr>

          {expenses.map((line) => (
            <tr className="c-statement__row" key={line.kind}>
              <th scope="row">
                {line.label}
                {line.derived && (
                  <span className="c-statement__note"> worked out from the stock ledger</span>
                )}
              </th>
              <td className="tnum">({formatMoney(line.amount)})</td>
            </tr>
          ))}

          <tr className="c-statement__row c-statement__row--subtotal">
            <th scope="row">Total costs</th>
            <td className="tnum">({formatMoney(total_expenses)})</td>
          </tr>

          <tr className="c-statement__row c-statement__row--total">
            <th scope="row">Surplus</th>
            <td className={surplus < 0 ? 'tnum c-money--due' : 'tnum'}>
              {formatMoney(surplus)}
              {margin !== null && <span className="c-statement__note"> · {margin}%</span>}
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

function ExclusionsCard() {
  return (
    <Card title="What this does not include">
      {/* Stated on the screen rather than in a comment nobody reads: these
          are the costs the app has no model for, and a reader who assumes
          otherwise over-reads the surplus. */}
      <ul className="c-prose-list">
        <li>Depreciation on equipment — the purchase is recorded, the write-down is not.</li>
        <li>
          Panel commissions. <code>Panel.discount_percentage</code> is a discount taken off the
          bill, not a fee paid out, and treating it as one would double-count.
        </li>
        <li>Anything nobody entered. The warning above names the categories that are empty.</li>
      </ul>
    </Card>
  );
}
